import dayjs from "dayjs";
import { matchSelectOption } from "./matchSelectOption";

/**
 * Works out which form fields a reviewer changed on the Edit Application
 * page, for the on-screen list and the confirmation box. The backend runs
 * the same comparison again on save and is the one that decides what is
 * written; see getFieldChanges() in
 * civic-plan-backend/api/reviewer-application-edit/reviewer-application-edit.service.js.
 */

export type ReviewerEditField = {
  uid: string;
  type: string;
  label?: string;
  options?: { id: number | string | boolean; name: string }[];
};

export type ReviewerEditFieldChange<F extends ReviewerEditField = ReviewerEditField> = {
  field: F;
  oldValue: string;
  newValue: string;
};

const NUMERIC_FIELD_TYPES = ["integer", "float", "area"];

function isBlank(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (typeof value === "number" && Number.isNaN(value))
  );
}

function toBoolean(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || value === "true";
}

/**
 * Compares by the field's type, so an untouched field never counts as a
 * change only because the form holds it in another shape (15 and "15", or an
 * empty box against a stored null).
 */
export function isSameFieldValue(
  field: ReviewerEditField,
  oldValue: unknown,
  newValue: unknown
): boolean {
  if (field.type === "boolean") return toBoolean(oldValue) === toBoolean(newValue);

  if (isBlank(oldValue) && isBlank(newValue)) return true;
  if (isBlank(oldValue) || isBlank(newValue)) return false;

  /**
   * A dropdown swaps a stored option name ("No", "9 m") for the option's id
   * ("181", "9982") when the form opens, so both values are resolved to the
   * option they stand for, with the dropdown's own matching, and the options
   * are compared. A value that matches no option (an old typed road width
   * such as "50") is compared as it is.
   * BEFORE: dropdown values fell through to the plain text comparison below,
   * so "No" and "181" counted as a change.
   */
  if (field.options?.length) {
    const oldOption = matchSelectOption(field.options, oldValue);
    const newOption = matchSelectOption(field.options, newValue);
    if (oldOption || newOption) {
      return (
        String(oldOption?.id ?? oldValue).trim() === String(newOption?.id ?? newValue).trim()
      );
    }
  }

  if (NUMERIC_FIELD_TYPES.includes(field.type)) {
    return Number(oldValue) === Number(newValue);
  }

  if (field.type === "date") {
    return (
      dayjs(oldValue as string).format("YYYY-MM-DD") ===
      dayjs(newValue as string).format("YYYY-MM-DD")
    );
  }

  const normalise = (value: unknown) => {
    const text = String(value).trim();
    /** Plot No. is saved without leading zeros, so "015" and "15" are the same plot. */
    return field.uid === "plot_no" ? text.replace(/^0+(?=\d)/, "") : text;
  };
  return normalise(oldValue) === normalise(newValue);
}

/** The value as a person reads it. */
export function toDisplayFieldValue(field: ReviewerEditField, value: unknown): string {
  if (field.type === "boolean") {
    if (isBlank(value)) return "";
    return toBoolean(value) ? "Yes" : "No";
  }
  if (isBlank(value)) return "";
  if (field.type === "date") return dayjs(value as string).format("DD-MM-YYYY");

  const option = (field.options || []).find((opt) => String(opt.id) === String(value));
  return String(option?.name ?? value);
}

export function getReviewerEditChanges<F extends ReviewerEditField>(
  fields: F[],
  original: Record<string, unknown>,
  current: Record<string, unknown>
): ReviewerEditFieldChange<F>[] {
  return fields
    .filter((field) => !isSameFieldValue(field, original?.[field.uid], current?.[field.uid]))
    .map((field) => ({
      field,
      oldValue: toDisplayFieldValue(field, original?.[field.uid]),
      newValue: toDisplayFieldValue(field, current?.[field.uid]),
    }));
}
